import type { Coords } from '@/shared/graphql/router/graphql'

interface Point {
  x: number
  y: number
}

function mergeCoordsGroup(coords: Coords[]): Coords {
  if (coords.length === 1) return coords[0]

  return {
    x1: Math.min(...coords.map((c) => c.x1)),
    y1: Math.min(...coords.map((c) => c.y1)),
    x2: Math.max(...coords.map((c) => c.x2)),
    y2: Math.max(...coords.map((c) => c.y2)),
    page: coords[0].page,
  }
}

function mergeHorizontalCoords(coords: Coords[]): Coords[] {
  if (!coords || coords.length === 0) return []
  if (coords.length === 1) return coords

  const sorted = [...coords].sort((a, b) => {
    const centerDiff = (a.y1 + a.y2) / 2 - (b.y1 + b.y2) / 2
    const minHeight = Math.min(a.y2 - a.y1, b.y2 - b.y1)
    if (Math.abs(centerDiff) <= Math.max(1, minHeight * 0.35)) {
      return a.x1 - b.x1
    }
    return centerDiff
  })

  const merged: Coords[] = []
  let currentLine: Coords[] = [sorted[0]]

  for (let i = 1; i < sorted.length; i++) {
    const curr = sorted[i]
    const lastInLine = currentLine[currentLine.length - 1]
    const centerDelta = Math.abs(
      (curr.y1 + curr.y2) / 2 - (lastInLine.y1 + lastInLine.y2) / 2
    )
    const minHeight = Math.min(curr.y2 - curr.y1, lastInLine.y2 - lastInLine.y1)

    // PDF font boxes can overlap adjacent rows when the font's ascender and
    // descender metrics are taller than the line pitch. Center proximity, not
    // rectangle overlap, determines whether fragments belong to one row.
    if (centerDelta <= Math.max(1, minHeight * 0.35)) {
      currentLine.push(curr)
    } else {
      merged.push(mergeCoordsGroup(currentLine))
      currentLine = [curr]
    }
  }
  merged.push(mergeCoordsGroup(currentLine))

  return merged
}

export function groupCoordsIntoBlocks(coords: Coords[]): Coords[][] {
  if (!coords || coords.length === 0) return []

  const mergedLines = mergeHorizontalCoords(coords)

  if (mergedLines.length === 1) return [[mergedLines[0]]]

  const sorted = [...mergedLines].sort((a, b) => a.y1 - b.y1)

  const heights = sorted.map((c) => c.y2 - c.y1)
  const avgHeight = heights.reduce((a, b) => a + b, 0) / heights.length
  const gapThreshold = avgHeight * 1.5

  const blocks: Coords[][] = []
  let currentBlock: Coords[] = [sorted[0]]

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    const curr = sorted[i]
    const gap = curr.y1 - prev.y2

    if (gap > gapThreshold) {
      blocks.push(currentBlock)
      currentBlock = [curr]
    } else {
      currentBlock.push(curr)
    }
  }
  blocks.push(currentBlock)

  return blocks
}

export function highlightPaddingForDisplay(
  naturalWidth: number,
  naturalHeight: number,
  renderedWidth: number,
  renderedHeight: number,
  screenClearance = 3
): number {
  if (
    naturalWidth <= 0 ||
    naturalHeight <= 0 ||
    renderedWidth <= 0 ||
    renderedHeight <= 0
  ) {
    return 2
  }

  const imagePixelsPerScreenPixel = Math.max(
    naturalWidth / renderedWidth,
    naturalHeight / renderedHeight
  )
  return Math.max(2, screenClearance * imagePixelsPerScreenPixel)
}

export function generateBlockPath(
  coords: Pick<Coords, 'x1' | 'y1' | 'x2' | 'y2'>[],
  baseWidth: number,
  baseHeight: number,
  cornerRadius: number,
  padding: number
): string {
  if (!coords || coords.length === 0) return ''

  const toPercent = (val: number, base: number) => (val / base) * 100

  const sorted = [...coords]
    .sort((a, b) => a.y1 - b.y1)
    .map((coord) => ({
      x1: coord.x1 - padding,
      y1: coord.y1 - padding,
      x2: coord.x2 + padding,
      y2: coord.y2 + padding,
    }))

  // Meet adjacent rows at the midpoint of their original vertical edges. The
  // display padding cancels out in this average, so wrapped-line transitions
  // remain in the source whitespace instead of following a padded edge through
  // the preceding or following glyphs.
  for (let i = 1; i < sorted.length; i++) {
    const boundary = (sorted[i - 1].y2 + sorted[i].y1) / 2
    sorted[i - 1].y2 = boundary
    sorted[i].y1 = boundary
  }

  const points: Point[] = []
  const pushPoint = (x: number, y: number) => {
    const previous = points[points.length - 1]
    if (!previous || previous.x !== x || previous.y !== y) {
      points.push({ x, y })
    }
  }

  const first = sorted[0]
  pushPoint(toPercent(first.x1, baseWidth), toPercent(first.y1, baseHeight))

  // Trace the union's left edge downward. A line extending farther left starts
  // at its top edge; an indented next line starts after the prior line ends.
  for (let i = 0; i + 1 < sorted.length; i++) {
    const current = sorted[i]
    const next = sorted[i + 1]
    const transitionY = next.x1 < current.x1 ? next.y1 : current.y2
    pushPoint(
      toPercent(current.x1, baseWidth),
      toPercent(transitionY, baseHeight)
    )
    pushPoint(toPercent(next.x1, baseWidth), toPercent(transitionY, baseHeight))
  }

  const last = sorted[sorted.length - 1]
  pushPoint(toPercent(last.x1, baseWidth), toPercent(last.y2, baseHeight))
  pushPoint(toPercent(last.x2, baseWidth), toPercent(last.y2, baseHeight))

  // Trace the union's right edge upward using the corresponding outer edge for
  // each line ending. Horizontal steps therefore remain outside the text.
  for (let i = sorted.length - 2; i >= 0; i--) {
    const current = sorted[i]
    const next = sorted[i + 1]
    const transitionY = next.x2 > current.x2 ? next.y1 : current.y2
    pushPoint(toPercent(next.x2, baseWidth), toPercent(transitionY, baseHeight))
    pushPoint(
      toPercent(current.x2, baseWidth),
      toPercent(transitionY, baseHeight)
    )
  }
  pushPoint(toPercent(first.x2, baseWidth), toPercent(first.y1, baseHeight))

  return buildRoundedPolygonPath(points, cornerRadius)
}

export interface LineRect {
  x1: number
  y1: number
  x2: number
  y2: number
}

export function measureLineRects(
  textEl: HTMLElement,
  originEl: HTMLElement
): LineRect[] {
  const range = document.createRange()
  range.selectNodeContents(textEl)
  const origin = originEl.getBoundingClientRect()

  const lines: LineRect[] = []
  for (const rect of Array.from(range.getClientRects())) {
    if (rect.width <= 0 || rect.height <= 0) continue

    const next: LineRect = {
      x1: rect.left - origin.left,
      y1: rect.top - origin.top,
      x2: rect.right - origin.left,
      y2: rect.bottom - origin.top,
    }
    const center = (next.y1 + next.y2) / 2
    const sameLine = lines.find(
      (line) => Math.abs((line.y1 + line.y2) / 2 - center) < rect.height * 0.6
    )

    if (sameLine) {
      sameLine.x1 = Math.min(sameLine.x1, next.x1)
      sameLine.y1 = Math.min(sameLine.y1, next.y1)
      sameLine.x2 = Math.max(sameLine.x2, next.x2)
      sameLine.y2 = Math.max(sameLine.y2, next.y2)
    } else {
      lines.push(next)
    }
  }

  return lines
}

export function buildHighlightPath(
  rects: LineRect[],
  cornerRadius = 4,
  paddingX = 2,
  paddingY = 1
): string {
  if (!rects.length) return ''

  const sorted = [...rects]
    .sort((a, b) => a.y1 - b.y1)
    .map((rect) => ({
      x1: rect.x1 - paddingX,
      y1: rect.y1 - paddingY,
      x2: rect.x2 + paddingX,
      y2: rect.y2 + paddingY,
    }))

  for (let i = 1; i < sorted.length; i++) {
    const boundary = (sorted[i - 1].y2 + sorted[i].y1) / 2
    sorted[i - 1].y2 = boundary
    sorted[i].y1 = boundary
  }

  const left = Math.min(...sorted.map((rect) => rect.x1))
  const top = sorted[0].y1
  const bottom = sorted[sorted.length - 1].y2

  const points: Point[] = [
    { x: left, y: top },
    { x: sorted[0].x2, y: top },
  ]

  let curX = sorted[0].x2
  for (let i = 1; i < sorted.length; i++) {
    if (Math.abs(sorted[i].x2 - curX) > 0.5) {
      points.push({ x: curX, y: sorted[i].y1 })
      points.push({ x: sorted[i].x2, y: sorted[i].y1 })
      curX = sorted[i].x2
    }
  }

  points.push({ x: curX, y: bottom })
  points.push({ x: left, y: bottom })

  return buildRoundedPolygonPath(points, cornerRadius)
}

function buildRoundedPolygonPath(points: Point[], radius: number): string {
  if (!points || points.length < 3) return ''

  const n = points.length
  const pathParts: string[] = []

  for (let i = 0; i < n; i++) {
    const prev = points[(i - 1 + n) % n]
    const curr = points[i]
    const next = points[(i + 1) % n]

    const v1 = { x: curr.x - prev.x, y: curr.y - prev.y }
    const v2 = { x: next.x - curr.x, y: next.y - curr.y }

    const len1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y)
    const len2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y)

    if (len1 === 0 || len2 === 0) continue

    const maxR = Math.min(len1 / 2, len2 / 2, radius)

    const p1 = {
      x: curr.x - (v1.x / len1) * maxR,
      y: curr.y - (v1.y / len1) * maxR,
    }
    const p2 = {
      x: curr.x + (v2.x / len2) * maxR,
      y: curr.y + (v2.y / len2) * maxR,
    }

    if (i === 0) {
      pathParts.push(`M ${p1.x.toFixed(3)} ${p1.y.toFixed(3)}`)
    } else {
      pathParts.push(`L ${p1.x.toFixed(3)} ${p1.y.toFixed(3)}`)
    }

    pathParts.push(
      `Q ${curr.x.toFixed(3)} ${curr.y.toFixed(3)} ${p2.x.toFixed(3)} ${p2.y.toFixed(3)}`
    )
  }

  pathParts.push('Z')
  return pathParts.join(' ')
}
